'use client';

import { useEffect, useRef } from 'react';

// Imports des outils EditorJS
import { PrimaryButton } from '@components/buttons';
import Anchor from '@coolbytes/editorjs-anchor';
import Delimiter from '@coolbytes/editorjs-delimiter';
import AttachesTool from '@editorjs/attaches';
import Checklist from '@editorjs/checklist';
import CodeTool from '@editorjs/code';
import EditorJS from '@editorjs/editorjs';
import Embed from '@editorjs/embed';
import ImageTool from '@editorjs/image';
import InlineCode from '@editorjs/inline-code';
import LinkTool from '@editorjs/link';
import List from '@editorjs/list';
import NestedList from '@editorjs/nested-list';
import Quote from '@editorjs/quote';
import Table from '@editorjs/table';
import TextVariantTune from '@editorjs/text-variant-tune';
import Underline from '@editorjs/underline';
import Warning from '@editorjs/warning';
import Strikethrough from '@sotaproject/strikethrough';
import { useLocalStore } from '@store/useLocalStore';
import Annotation from 'editorjs-annotation';
import EditorButtons from 'editorjs-button';
import ChangeCase from 'editorjs-change-case';
import Header from 'editorjs-header-with-alignment';
import NoticeTune from 'editorjs-notice';
import Paragraph from 'editorjs-paragraph-with-alignment';
import ColorPlugin from 'editorjs-text-color-plugin';
import ToggleBlock from 'editorjs-toggle-block';
import Tooltip from 'editorjs-tooltip';




export const CustomEditorJS = ({ data, onChange, onSubmit, readOnly = false, autoFocus = true, isEditorUpdated, className = "", type, id, isAnswer }) => {
    const editorRef = useRef(null);
    const token = useLocalStore(state => state.user.token);
    // const regex = new RegExp(`https?:\/\/${process.env.SERVER_ADRESS}\/uploads\/medias\/([^\/]+)`);
    const regex = /https?:\/\/myhostaapp.s3.eu-west-3.amazonaws.com\/([^\/]+)/;



    const initRef = useRef(false); // Used to prevent ReactStrictMode double rendering in development

    const initEditor = () => {

        const editor = new EditorJS({
            holder: "editorjs",
            data,
            autofocus: autoFocus,
            readOnly: readOnly,
            onChange: async () => {
                const dataSauvegardee = await editor.save();
                onChange(dataSauvegardee); // Propagation des données au parent
            },
            onReady: () => {
                editorRef.current = editor;
            },

            i18n: {
                messages: {
                    ui: {
                        blockTunes: {
                            toggler: {
                                'Click to tune': 'Cliquez pour ajuster',
                                'or drag to move': 'ou glissez pour déplacer',
                            },
                        },
                        inlineToolbar: {
                            converter: {
                                'Convert to': 'Convertir en',
                            },
                            strikethrough: 'Barré',
                            underline: 'Souligner',
                        },
                        toolbar: {
                            toolbox: {
                                Add: 'Ajouter',
                            },
                            strikethrough: 'Barré',
                            underline: 'Souligner',
                            color: 'Couleur',
                            converter: {
                                'Convert to': 'Convertir en',
                            },
                        },
                    },
                    toolNames: {
                        Text: 'Paragraphe',
                        Heading: 'Titre',
                        List: 'Liste',
                        Warning: 'Avertissement',
                        Checklist: 'Liste de contrôle',
                        Quote: 'Citation',
                        Code: 'Code',
                        Delimiter: 'Séparateur',
                        'Raw HTML': 'HTML brut',
                        Table: 'Tableau',
                        Link: 'Lien',
                        Marker: 'Surligneur',
                        Bold: 'Gras',
                        Italic: 'Italique',
                        InlineCode: 'Code en ligne',
                        Attachment: 'Pièce jointe',
                        Button: 'Bouton',
                        Toggle: 'Basculer',
                        Anchor: 'Ancre',
                    },
                    tools: {
                        anchor: {
                            'Add anchor': 'Ajouter une ancre', // Message pour ajouter une ancre
                            'Anchor name': 'Nom de l\'ancre', // Texte pour le champ de nom de l'ancre
                            'Link to anchor': 'Lien vers l\'ancre', // Message pour créer un lien vers une ancre
                            'Insert anchor': 'Insérer l\'ancre', // Texte pour insérer l'ancre
                        },
                        warning: {
                            Title: 'Titre',
                            Message: 'Message',
                        },
                        link: {
                            'Add a link': 'Ajouter un lien',
                        },
                        stub: {
                            'The block can not be displayed correctly.': 'Le bloc ne peut pas être affiché correctement',
                        },
                    },
                    blockTunes: {
                        delete: {
                            Delete: 'Supprimer', // Texte pour la suppression d'un bloc
                        },
                        moveUp: {
                            'Move up': 'Déplacer vers le haut', // Texte pour déplacer le bloc vers le haut
                        },
                        moveDown: {
                            'Move down': 'Déplacer vers le bas', // Texte pour déplacer le bloc vers le bas
                        },
                        settings: {
                            'Block settings': 'Paramètres du bloc', // Texte pour accéder aux paramètres du bloc
                        },
                        duplicate: {
                            'Duplicate': 'Dupliquer', // Texte pour dupliquer le bloc
                        },
                        alignment: {
                            left: 'Aligner à gauche', // Texte pour aligner le bloc à gauche
                            center: 'Centrer', // Texte pour centrer le bloc
                            right: 'Aligner à droite', // Texte pour aligner le bloc à droite
                        },
                        resize: {
                            small: 'Petit', // Texte pour la taille réduite du bloc
                            medium: 'Moyenne', // Texte pour la taille moyenne du bloc
                            large: 'Grand', // Texte pour la taille large du bloc
                        },
                        toggleVisibility: {
                            show: 'Montrer', // Texte pour rendre un bloc visible
                            hide: 'Cacher', // Texte pour cacher un bloc
                        },
                        actions: {
                            'Remove': 'Retirer', // Texte pour retirer l'action du bloc
                            'Restore': 'Restaurer', // Texte pour restaurer une action
                        },
                        tooltips: {
                            'Click to adjust': 'Cliquez pour ajuster', // Tooltip qui apparaît pour ajuster le bloc
                            'or drag to move': 'ou faites glisser pour déplacer', // Tooltip pour déplacer un bloc
                        },
                        options: {
                            'Move to top': 'Déplacer en haut', // Option pour déplacer le bloc en haut
                            'Move to bottom': 'Déplacer en bas', // Option pour déplacer le bloc en bas
                        }
                    }
                },
            },

            tools: {
                header: {
                    class: Header,
                    config: {
                        placeholder: 'Entrez un titre...',
                        levels: [1, 2, 3, 4, 5, 6], // Niveaux de titre
                        defaultLevel: 1, // Niveau de titre par défaut
                        defaultAlignment: 'left', // Alignement par défaut du titre
                    },
                },
                image: {
                    class: ImageTool,
                    config: {
                        uploader: {
                            uploadByFile: async function (file) {
                                try {
                                    // Créez un objet FormData pour envoyer le fichier
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    
                                    // Effectuez l'upload du fichier en utilisant fetch avec async/await
                                    // const response = await fetch(`${process.env.SERVER_ADRESS}/yoopta/file`, {
                                    //     method: 'POST',
                                    //     headers: {
                                    //         'Authorization': `Bearer ${token}`  // Token d'authentification
                                    //     },
                                    //     body: formData, // Envoyer le fichier via FormData
                                    // });
                                    const response = await fetch(`${process.env.SERVER_ADRESS}/yoopta/file/${id}`, {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                        },
                                        body: formData,
                                      });

                                    // Vérifiez que la réponse est valide
                                    const data = await response.json();

                                    // Si l'upload a réussi, retournez l'URL et le nom du fichier
                                    if (data.result) {
                                        return {
                                            success: 1,  // L'upload a réussi
                                            file: {
                                                url: data.url,  // URL du fichier retournée par le serveur
                                            }
                                        };
                                    } else {
                                        // Si l'upload échoue, lancez une erreur
                                        throw new Error('Upload échoué');
                                    }
                                } catch (error) {
                                    // Capturez et affichez les erreurs
                                    console.error('Erreur d\'upload', error);
                                    return { success: 0 };  // L'upload a échoué
                                }
                            },
                        },
                    },
                },
                
                linkTool: {
                    class: LinkTool,
                    config: {
                      endpoint: `${process.env.SERVER_ADRESS}/yoopta/fetchUrl/`, // Your backend endpoint for url data fetching,
                     
                    },
                    
                  },
                list: List,
                quote: Quote,
                strikethrough: Strikethrough,
                code: CodeTool,
                embed: {
                    class: Embed,
                    config: {
                        services: {
                            youtube: true,
                            vimeo: true,
                            coub: true,
                            hosta: {
                                regex: regex,
                                embedUrl: `http://myhostaapp.s3.eu-west-3.amazonaws.com/<%= remote_id %>`,
                                html: "<video width='100%' height='300' controls controlslist='nodownload noremoteplayback  foobar'  oncontextmenu='return false;'style='border: none; display: block; margin: 0 auto;' > <source src='path/to/your-video.mp4' type='video/mp4'>Votre navigateur ne supporte pas la balise <code>video</code>.</video> width: 600",
                                id: (groups) => groups[0],
                            },

                        },
                    },
                },
                warning: Warning,
                delimiter: {
                    class: Delimiter,
                    config: {
                        styleOptions: ['star', 'dash', 'line'],
                        defaultStyle: 'star',
                        lineWidthOptions: [8, 15, 25, 35, 50, 60, 100],
                        defaultLineWidth: 25,
                        lineThicknessOptions: [1, 2, 3, 4, 5, 6],
                        defaultLineThickness: 2,
                    }
                },
                paragraph: {
                    class: Paragraph,
                    inlineToolbar: true,
                },
                nestedList: NestedList,
                checklist: Checklist,
                table: Table,
                anchor: Anchor,
                noticeTune: NoticeTune,
                annotation: Annotation,
                textVariant: TextVariantTune,
                attaches: {
                    class: AttachesTool,
                    config: {
                        uploader: {
                            uploadByFile: async function (file) {
                                try {
                                    // Créez un objet FormData pour envoyer le fichier
                                    const formData = new FormData();
                                    formData.append('file', file);

                                    // Effectuez l'upload du fichier en utilisant fetch avec async/await
                                    const response = await fetch(`${process.env.SERVER_ADRESS}/yoopta/file/${id}`, {
                                        method: 'POST',
                                        headers: {
                                            'Authorization': `Bearer ${token}`  // Token d'authentification
                                        },
                                        body: formData, // Envoyer le fichier via FormData
                                    });

                                    // Vérifiez que la réponse est valide
                                    const data = await response.json();

                                    // Si l'upload a réussi, retournez l'URL et le nom du fichier
                                    if (data.result) {
                                        return {
                                            success: 1,  // L'upload a réussi
                                            file: {
                                                url: data.url,  // URL du fichier retournée par le serveur
                                                name: data.name  // Nom du fichier (ou autre information)
                                            }
                                        };
                                    } else {
                                        // Si l'upload échoue, lancez une erreur
                                        throw new Error('Upload échoué');
                                    }
                                } catch (error) {
                                    // Capturez et affichez les erreurs
                                    console.error('Erreur d\'upload', error);
                                    return { success: 0 };  // L'upload a échoué
                                }
                            }
                        }

                    }
                },
                editorButtons: EditorButtons,
                underline: Underline,
                inlineCode: {
                    class: InlineCode,
                    shortcut: 'CMD+SHIFT+M',
                },
                tooltip: Tooltip,
                changeCase: ChangeCase,
                Color: {
                    class: ColorPlugin, // if load from CDN, please try: window.ColorPlugin
                    config: {
                        colorCollections: ['#EC7878', '#9C27B0', '#673AB7', '#3F51B5', '#0070FF', '#03A9F4', '#00BCD4', '#4CAF50', '#8BC34A', '#CDDC39', '#FFF'],
                        defaultColor: '#FF1300',
                        type: 'text',
                        customPicker: true // add a button to allow selecting any colour  
                    }
                },
                Marker: {
                    class: ColorPlugin, // if load from CDN, please try: window.ColorPlugin
                    config: {
                        defaultColor: '#FFBF00',
                        type: 'marker',
                    }
                },
                toggle: {
                    class: ToggleBlock,
                    inlineToolbar: true,
                },
            },
            
            tunes: ['anchor', 'noticeTune', 'textVariant'],
            inlineToolbar:true,
        });
    };


    useEffect(() => {
        if (initRef.current) return; // Prevent double initialization

        initRef.current = true; // Set the flag to avoid double initialization
        if (!editorRef.current) {
            initEditor();
        }
        return () => {
            editorRef?.current?.destroy();
            editorRef.current = null;
        };
    }, []);

    return (
        <div className="flex w-full flex-col items-center">
            {isEditorUpdated && <p className='mb-8 w-full text-center font-semibold text-success'>Les données ont été mises à jour</p>}
            <div className={`w-full !max-w-[800px] rounded-lg ${isEditorUpdated ? "border-1 border-success" : ""} bg-white px-16 pt-4 shadow-md ${className}`}>
                <div id="editorjs"></div>
            </div>
            {!readOnly &&
                <div className="mb-4 mt-12 flex w-full max-w-[800px] flex-row-reverse">
                    <PrimaryButton onClick={onSubmit}>Enregistrer</PrimaryButton>
                </div>
            }
        </div>
    );
};